import { OrderProps } from "store/modules/Orders/types.store";
import { formatCnpj, normalizeCnpjDocument } from "./cnpj";

export function formatCurrency(currency: string | number): string {
  return Number(currency)?.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

export const EMAIL_PATTERN_REGEX =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export function cpfOrCnpjFormat(cpfOrCnpj: string | number): string {
  cpfOrCnpj = cpfOrCnpj?.toString();

  if (cpfOrCnpj?.length !== 11) {
    const normalized = normalizeCnpjDocument(cpfOrCnpj || "");
    const formattedCnpj = formatCnpj(normalized);

    if (formattedCnpj !== normalized) {
      cpfOrCnpj = formattedCnpj;
    } else {
      cpfOrCnpj = cpfOrCnpj?.replace(
        /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
        "$1.$2.$3/$4-$5",
      );
    }
  } else {
    cpfOrCnpj = cpfOrCnpj?.replace(
      /(\d{3})(\d{3})(\d{3})(\d{2})/,
      "$1.$2.$3-$4",
    );
  }
  return cpfOrCnpj;
}

export function maskCpf(cpf: string | undefined) {
  if (!cpf) {
    return "";
  }
  /* eslint-disable prefer-destructuring */
  const num = cpf.replace(/[^\d]/g, "").slice(0, 11);
  const length = num.length;

  if (length <= 6) {
    cpf = num.replace(/(\d{3})(\d{1,3})/g, "$1.$2");
  } else if (length <= 9) {
    cpf = num.replace(/(\d{3})(\d{3})(\d{1,3})/g, "$1.$2.$3");
  } else if (length <= 11) {
    cpf = num.replace(/(\d{3})(\d{3})(\d{3})(\d{1,2})/g, "$1.$2.$3-$4");
  } else if (length >= 12) {
    cpf = cpf.substr(0, 14);
  }
  return cpf;
}

export function cellPhoneBr(cell: string): string {
  let v = cell.replace(/\D/g, "");

  v = v.replace(/^(\d{2})(\d)/g, "($1) $2");
  v = v.replace(/(\d)(\d{4})$/, "$1-$2");
  return v;
}

export const PHONE_REGEX = /^(\d{2})(\d+)(\d{4})$/;

export const removeCaracterPhone = (phone: string) =>
  phone ? phone.replace(/[^0-9]+/g, "") : "";

export function formatPhoneByText(phone: string) {
  phone = phone?.replace(/\D/g, "");
  phone = phone?.replace(/^(\d{2})(\d)/g, "($1) $2");
  phone = phone?.replace(/(\d)(\d{4})$/, "$1-$2");
  return phone;
}

export const copyDivToClipboard = (id: string) => {
  const range = document.createRange();
  const element = document?.getElementById(id);

  if (element) {
    range.selectNode(element as any);
    window?.getSelection()?.removeAllRanges();
    window?.getSelection()?.addRange(range);
    document.execCommand("copy");
    window?.getSelection()?.removeAllRanges();
  }
};

export const voucherFunction = (data: OrderProps) => {
  if (data?.voucher?.campaign) {
    return data?.voucher?.campaign;
  }
  if (data?.voucher?.code) {
    return data?.voucher?.code;
  }
  return "";
};

export const copyToClipboard = (value: string) => {
  if (navigator.clipboard !== undefined) {
    navigator.clipboard.writeText(value);
    return true;
  }

  return false;
};

export const totalPrices = (data: OrderProps) => {
  let sum = 0;
  if (data) {
    for (let i = 0; i < data?.items?.length; i += 1) {
      sum +=
        (data?.items?.[i]?.unitPrice || 0) * (data?.items?.[i]?.quantity || 0);
    }
  }
  return sum;
};

export function normalizeForComparison(text: string): string {
  return (text || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

type AddressInput = {
  street?: string | null;
  number?: string | null;
  complement?: string | null;
  apartment?: string | null;
  district?: string | null;
  neighborhood?: string | null;
  city?: string | null;
  state?: string | null;
};

const isFilledText = (value?: string | null): value is string =>
  typeof value === "string" && value.trim().length > 0;

const firstFilled = (...values: (string | null | undefined)[]): string => {
  const match = values.find(isFilledText);
  return match ? match.trim() : "";
};

export function formatStreetAddress(
  address?: AddressInput | null,
  options: { fallback?: string; complementSeparator?: string } = {},
): string {
  const { fallback = "", complementSeparator = " / " } = options;
  if (!address) return fallback;

  const street = firstFilled(address.street);
  const number = firstFilled(address.number);
  const complement = firstFilled(address.complement, address.apartment);

  const streetAndNumber = [street, number].filter(Boolean).join(", ");
  const result = [streetAndNumber, complement]
    .filter(Boolean)
    .join(complementSeparator);

  return result || fallback;
}

export function formatFullAddress(
  address?: AddressInput | null,
  options: { fallback?: string } = {},
): string {
  const { fallback = "" } = options;
  if (!address) return fallback;

  const head = formatStreetAddress(address);
  const district = firstFilled(address.district, address.neighborhood);
  const city = firstFilled(address.city);
  const state = firstFilled(address.state);

  const cityState = [city, state].filter(Boolean).join(", ");
  const tail = [district, cityState].filter(Boolean).join(" - ");

  return [head, tail].filter(Boolean).join(", ") || fallback;
}
